import { motion, useReducedMotion } from 'motion/react'
import { spring } from '../lib/springs'

/* ============================================================================
   Notification stack — adapted from beui.dev/components/blocks/notification-
   stack (TypeScript + Tailwind there; plain CSS here, the `.ns-*` block at the
   end of theme.css).

   It has no trigger of its own: it sits absolutely on top of the row that
   owns it (the sidebar's Notifications row) and the parent decides `open`.
   Closed, the cards rest stacked on that row — one card, the next ones
   peeking under it — and invisible. Opening fades them in and fans them out
   upward into a list, so the stack looks like it grows out of the row.

   items:    [{ id, title, description?, trailing? }]
   onSelect: (id) => void, one card clicked
   ========================================================================== */

const STACK_PEEK = 8
const STACK_INSET = 12

const NotificationStack = ({ items, open, onSelect, maxVisible = 3, className = '' }) => {
  const reduce = useReducedMotion()
  const visibleItems = items.slice(0, Math.max(1, maxVisible))
  if (visibleItems.length === 0) return null

  const cardTransition = reduce ? { duration: 0 } : spring.moderate
  const layoutTransition = reduce ? { duration: 0 } : spring.moderate

  return (
    <motion.div
      className={'ns-pop' + (open ? ' is-open' : '') + (className ? ' ' + className : '')}
      initial={false}
      animate={{ opacity: open ? 1 : 0, y: open ? 0 : 6 }}
      transition={reduce ? { duration: 0 } : open ? spring.fast : spring.fast.exit}
      aria-hidden={!open}
    >
      <motion.span
        aria-hidden="true"
        layout
        initial={false}
        transition={layoutTransition}
        className="ns-bg"
      />
      <span className="ns-stack">
        {visibleItems.map((item, index) => {
          const isPrimary = index === 0
          return (
            <motion.button
              key={item.id}
              type="button"
              tabIndex={open ? 0 : -1}
              layout="position"
              initial={false}
              animate={{
                y: open ? 0 : index * STACK_PEEK,
                clipPath: open
                  ? 'inset(0px 0px round 16px)'
                  : `inset(0px ${index * STACK_INSET}px round 16px)`,
              }}
              transition={{ ...cardTransition, layout: layoutTransition }}
              className="ns-card"
              style={{
                zIndex: visibleItems.length - index,
                gridColumn: 1,
                // newest at the bottom, nearest the row it grows out of
                gridRow: open ? visibleItems.length - index : 1,
              }}
              onClick={() => onSelect(item.id)}
            >
              <span className={'ns-content' + (!isPrimary && !open ? ' is-hidden' : '')}>
                <span className="ns-head">
                  <span className="ns-title">{item.title}</span>
                  {item.trailing ? <span className="ns-trailing">{item.trailing}</span> : null}
                </span>
                {item.description ? <span className="ns-desc">{item.description}</span> : null}
              </span>
            </motion.button>
          )
        })}
      </span>
    </motion.div>
  )
}

export default NotificationStack
